import React, { useState, useRef, useEffect } from 'react';

interface ProgressiveImageProps {
    src: string;
    alt: string;
    className?: string;
    blurAmount?: number;
    priority?: boolean;
}

const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
    src,
    alt,
    className = '',
    blurAmount = 3,
    priority = false
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [blurPreviewLoaded, setBlurPreviewLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const blurImgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const spinnerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);

                        // Load blur preview immediately
                        const blurImg = new Image();
                        blurImg.onload = () => setBlurPreviewLoaded(true);
                        blurImg.onerror = () => { }; // Silently fail, use gradient fallback
                        blurImg.src = src; // Load same image but will render small and blurred

                        // Show spinner after 800ms if main image hasn't loaded yet
                        spinnerTimeoutRef.current = setTimeout(() => {
                            if (!isLoaded && !hasError) {
                                setShowSpinner(true);
                            }
                        }, 800);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '50px', // Start loading 50px before the image comes into view
                threshold: 0.1
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            observer.disconnect();
            if (spinnerTimeoutRef.current) {
                clearTimeout(spinnerTimeoutRef.current);
            }
        };
    }, [isLoaded, hasError, src]);

    const handleLoad = () => {
        setIsLoaded(true);
        setShowSpinner(false);
        // Clear spinner timeout if image loads quickly
        if (spinnerTimeoutRef.current) {
            clearTimeout(spinnerTimeoutRef.current);
            spinnerTimeoutRef.current = null;
        }
    };

    const handleError = () => {
        setHasError(true);
        setIsLoaded(true);
        setShowSpinner(false);
        setBlurPreviewLoaded(false); // Hide blur preview on error
        // Clear spinner timeout on error
        if (spinnerTimeoutRef.current) {
            clearTimeout(spinnerTimeoutRef.current);
            spinnerTimeoutRef.current = null;
        }
    };

    return (
        <div
            ref={containerRef}
            className={`relative overflow-hidden ${className}`}
        >
            {/* Simple gradient placeholder (same as skeleton) */}
            {isInView && !isLoaded && !hasError && !blurPreviewLoaded && (
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
            )}

            {/* Blurry image preview */}
            {isInView && !isLoaded && !hasError && blurPreviewLoaded && (
                <img
                    ref={blurImgRef}
                    src={src}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain transition-opacity duration-500"
                    style={{
                        filter: `blur(${blurAmount * 4}px) brightness(1.1)`,
                        transform: 'scale(1.05)', // Slightly scale to hide blur edges
                        opacity: showSpinner ? 0.7 : 1
                    }}
                    loading="eager"
                    onError={() => setBlurPreviewLoaded(false)} // Fall back to gradient
                />
            )}

            {/* Main image with simple lazy loading */}
            {isInView && (
                <img
                    ref={imgRef}
                    src={src}
                    alt={alt}
                    className={`relative w-full h-full object-contain transition-opacity duration-500 bg-accent-sand ${isLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                    loading={priority ? 'eager' : 'lazy'}
                    onLoad={handleLoad}
                    onError={handleError}
                    decoding="async"
                />
            )}

            {/* Loading state for when image is not in view yet */}
            {!isInView && (
                <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
            )}

            {/* Error state - only show after some delay to avoid flickering */}
            {hasError && isInView && (
                <div className="absolute inset-0 flex items-center justify-center bg-accent-sand">
                    <div className="text-center text-text-description">
                        <svg
                            className="mx-auto h-8 w-8 mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <p className="text-xs">Image unavailable</p>
                    </div>
                </div>
            )}

            {/* Loading spinner - only show after delay */}
            {showSpinner && isInView && !isLoaded && !hasError && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-accent-taupe border-t-secondary-500 rounded-full animate-spin shadow-sm" />
                </div>
            )}
        </div>
    );
};

export default ProgressiveImage;